/**
 * The scene model, version 1. The scene is the source of truth while editing; the OGraf package is compiled from it
 * and carries it (scene.json, and embedded in graphic.mjs).
 *
 * Conventions:
 * - lengths are canvas pixels, times are milliseconds from the start of a phase, angles are degrees;
 * - colours are `#RRGGBB` or `#RRGGBBAA`;
 * - every object is strict (unknown keys are errors) so a typo never silently drops a property; new optional
 *   properties can be added within sceneVersion 1, anything incompatible bumps sceneVersion with a migration.
 */
import { z } from 'zod';
import { layoutId, sameAspect, timeKey, timelinePhases, trackKey } from './layouts.ts';

export const SCENE_VERSION = 1;

/** Names for the reusable parts of the exported JSON Schema ($defs). */
export const sceneRegistry = z.registry<{ id: string }>();
const named = <T extends z.ZodType>(schema: T, id: string): T => {
  sceneRegistry.add(schema, { id });
  return schema;
};

// ---------------------------------------------------------------------------------------------------------------
// Primitives

export const IdSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,63}$/, 'ids start with a letter: letters, digits, _ and -');
export const FieldIdSchema = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'field ids are identifiers (they become manifest schema keys)');
export const ColorSchema = named(z.string().regex(/^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'colours are #RRGGBB or #RRGGBBAA'), 'Color');
export const AssetHashSchema = named(z.string().regex(/^sha256:[0-9a-f]{64}$/, 'asset references are sha256:<64 hex>'), 'AssetHash');
export const RationalSchema = named(z.strictObject({ num: z.int().positive(), den: z.int().positive() }), 'Rational');

const nonNegative = z.number().min(0);

// ---------------------------------------------------------------------------------------------------------------
// Canvas

export const CanvasSchema = z.strictObject({
  width: z.int().positive().max(7680),
  height: z.int().positive().max(4320),
  frameRate: RationalSchema,
  /** Reference background shown only in the editor; never exported into the graphic's rendering. */
  background: z
    .strictObject({ color: ColorSchema.optional(), asset: AssetHashSchema.optional() })
    .optional(),
});

// ---------------------------------------------------------------------------------------------------------------
// Element building blocks

export const TransformSchema = named(z.strictObject({
  x: z.number().default(0),
  y: z.number().default(0),
  rotation: z.number().default(0),
  scaleX: z.number().default(1),
  scaleY: z.number().default(1),
  /** Transform origin as a fraction of the element box (0.5, 0.5 = centre). */
  originX: z.number().default(0.5),
  originY: z.number().default(0.5),
  opacity: z.number().min(0).max(1).default(1),
}), 'Transform');

const Length = z.union([nonNegative, z.literal('auto')]);

/** Size. `auto` on a group makes it an auto-size box that hugs its content (plus padding), within min/max. */
export const SizeSchema = named(z.strictObject({
  width: Length.default('auto'),
  height: Length.default('auto'),
  minWidth: nonNegative.optional(),
  maxWidth: nonNegative.optional(),
  minHeight: nonNegative.optional(),
  maxHeight: nonNegative.optional(),
}), 'Size');

/**
 * Anchoring: which edge of the parent (or the canvas) x/y are measured from. x/y are always a translation from
 * the anchored position, so `right` with x = -96 pins the element 96 px inside the right edge.
 */
export const AnchorSchema = named(z.strictObject({
  horizontal: z.enum(['left', 'center', 'right', 'stretch']).default('left'),
  vertical: z.enum(['top', 'middle', 'bottom', 'stretch']).default('top'),
  to: z.enum(['parent', 'canvas']).default('parent'),
}), 'Anchor');

export const FillSchema = named(z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('solid'), color: ColorSchema }),
  z.strictObject({
    type: z.literal('linear-gradient'),
    angle: z.number().default(90),
    stops: z.array(z.strictObject({ offset: z.number().min(0).max(1), color: ColorSchema })).min(2),
  }),
]), 'Fill');

export const StrokeSchema = named(z.strictObject({ color: ColorSchema, width: nonNegative }), 'Stroke');
export const ShadowSchema = named(z.strictObject({
  color: ColorSchema,
  x: z.number().default(0),
  y: z.number().default(0),
  blur: nonNegative.default(0),
}), 'Shadow');

/** Per-corner radius: [top-left, top-right, bottom-right, bottom-left], or one number for all corners. */
export const CornerRadiusSchema = z.union([nonNegative, z.tuple([nonNegative, nonNegative, nonNegative, nonNegative])]);

export const ShapeStyleSchema = named(z.strictObject({
  fill: FillSchema.optional(),
  stroke: StrokeSchema.optional(),
  cornerRadius: CornerRadiusSchema.optional(),
  shadow: ShadowSchema.optional(),
  blur: nonNegative.optional(),
}), 'ShapeStyle');

export const TextStyleSchema = named(z.strictObject({
  /** Font asset (sha256 content hash). */
  font: AssetHashSchema,
  fontWeight: z.int().min(1).max(1000).default(400),
  fontStyle: z.enum(['normal', 'italic']).default('normal'),
  fontSize: z.number().positive(),
  lineHeight: z.number().positive().default(1.2),
  /** Tracking in px. */
  letterSpacing: z.number().default(0),
  textTransform: z.enum(['none', 'uppercase', 'lowercase', 'capitalize']).default('none'),
  textAlign: z.enum(['left', 'center', 'right']).default('left'),
  color: ColorSchema.default('#FFFFFF'),
  /** Glyph fill. A linear gradient paints the glyphs with it (color is then only the fallback); solid = color. */
  fill: FillSchema.optional(),
  stroke: StrokeSchema.optional(),
  shadow: ShadowSchema.optional(),
}), 'TextStyle');

/** What a text element does when its content does not fit its (max) width. Measured at load/update only. */
export const TextOverflowSchema = named(z.discriminatedUnion('mode', [
  z.strictObject({ mode: z.literal('none') }),
  z.strictObject({ mode: z.literal('shrink'), minFontSize: z.number().positive() }),
  z.strictObject({ mode: z.literal('wrap'), maxLines: z.int().positive() }),
  z.strictObject({ mode: z.literal('ellipsis') }),
]), 'TextOverflow');

export const StackSchema = z.strictObject({
  direction: z.enum(['horizontal', 'vertical']).default('vertical'),
  gap: nonNegative.default(0),
  align: z.enum(['start', 'center', 'end', 'stretch']).default('start'),
  justify: z.enum(['start', 'center', 'end', 'space-between']).default('start'),
});

export const PaddingSchema = z.strictObject({
  top: nonNegative.default(0),
  right: nonNegative.default(0),
  bottom: nonNegative.default(0),
  left: nonNegative.default(0),
});

export const GroupLayoutSchema = named(z.strictObject({
  /** Absolute: children are positioned by their transform and anchor. Stack: children flow (x/y offset them). */
  mode: z.enum(['absolute', 'stack']).default('absolute'),
  stack: StackSchema.optional(),
  padding: PaddingSchema.optional(),
}), 'GroupLayout');

// ---------------------------------------------------------------------------------------------------------------
// Elements

/**
 * CSS blend mode of an element. Over a transparent graphic a blend mode mixes with the graphic's own pixels only,
 * never with the video it is keyed over, so the editor warns when one is used (Broadcast quality requirements).
 */
export const BLEND_MODES = ['normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'difference', 'exclusion'] as const;
export const BlendModeSchema = z.enum(BLEND_MODES);

const elementBase = {
  id: IdSchema,
  name: z.string().max(200).default(''),
  locked: z.boolean().default(false),
  visible: z.boolean().default(true),
  transform: TransformSchema.default(() => TransformSchema.parse({})),
  size: SizeSchema.default(() => SizeSchema.parse({})),
  anchor: AnchorSchema.optional(),
  blendMode: BlendModeSchema.optional(),
};

export const TextElementSchema = named(z.strictObject({
  ...elementBase,
  type: z.literal('text'),
  text: z.string().default(''),
  style: TextStyleSchema,
  overflow: TextOverflowSchema.default({ mode: 'none' }),
}), 'TextElement');

export const RectElementSchema = named(z.strictObject({
  ...elementBase,
  type: z.literal('rect'),
  style: ShapeStyleSchema.default({}),
}), 'RectElement');

export const EllipseElementSchema = named(z.strictObject({
  ...elementBase,
  type: z.literal('ellipse'),
  style: ShapeStyleSchema.omit({ cornerRadius: true }).default({}),
}), 'EllipseElement');

/** A straight line of length size.width and thickness stroke.width; rotate it with transform.rotation. */
export const LineElementSchema = named(z.strictObject({
  ...elementBase,
  type: z.literal('line'),
  stroke: StrokeSchema,
  cap: z.enum(['butt', 'round']).default('butt'),
}), 'LineElement');

export const ImageElementSchema = named(z.strictObject({
  ...elementBase,
  type: z.literal('image'),
  asset: AssetHashSchema,
  fit: z.enum(['contain', 'cover', 'fill']).default('contain'),
}), 'ImageElement');

/**
 * Video: WebM (VP8/VP9, with alpha) or MP4 (H.264, no alpha). Muted. Its time is the time since the graphic became
 * visible (the first playAction): goToTime seeks it frame-exactly, real time plays it in step with the clock.
 * `loop` repeats it; otherwise it holds its last frame.
 */
export const VideoElementSchema = named(z.strictObject({
  ...elementBase,
  type: z.literal('video'),
  asset: AssetHashSchema,
  loop: z.boolean().default(true),
  fit: z.enum(['contain', 'cover', 'fill']).default('contain'),
}), 'VideoElement');

export interface GroupElement {
  id: string;
  name: string;
  locked: boolean;
  visible: boolean;
  transform: Transform;
  size: Size;
  anchor?: Anchor | undefined;
  blendMode?: BlendMode | undefined;
  type: 'group';
  layout: GroupLayout;
  style: ShapeStyle;
  clipContent: boolean;
  children: Element[];
}

/**
 * A clip mask: its children are visible only inside the mask shape (its own box: a rectangle with optional corner
 * radius, or an ellipse). Inset tracks on the mask reveal it (wipes); with fixedContent the children stay put while
 * the mask's own x/y animate, so the mask slides over them.
 */
export interface ClipMaskElement {
  id: string;
  name: string;
  locked: boolean;
  visible: boolean;
  transform: Transform;
  size: Size;
  anchor?: Anchor | undefined;
  blendMode?: BlendMode | undefined;
  type: 'clipMask';
  shape: 'rect' | 'ellipse';
  cornerRadius?: CornerRadius | undefined;
  fixedContent?: boolean | undefined;
  children: Element[];
}

export type TextElement = z.output<typeof TextElementSchema>;
export type RectElement = z.output<typeof RectElementSchema>;
export type EllipseElement = z.output<typeof EllipseElementSchema>;
export type LineElement = z.output<typeof LineElementSchema>;
export type ImageElement = z.output<typeof ImageElementSchema>;
export type VideoElement = z.output<typeof VideoElementSchema>;
export type Element =
  | GroupElement
  | ClipMaskElement
  | TextElement
  | RectElement
  | EllipseElement
  | LineElement
  | ImageElement
  | VideoElement;

export const GroupElementSchema = named(z.strictObject({
  ...elementBase,
  type: z.literal('group'),
  layout: GroupLayoutSchema.default({ mode: 'absolute' }),
  style: ShapeStyleSchema.default({}),
  clipContent: z.boolean().default(false),
  get children() {
    return z.array(ElementSchema).default([]);
  },
}), 'GroupElement');

export const ClipMaskElementSchema = named(z.strictObject({
  ...elementBase,
  type: z.literal('clipMask'),
  shape: z.enum(['rect', 'ellipse']).default('rect'),
  /** Rectangle masks: corner radius (one value or per corner). */
  cornerRadius: CornerRadiusSchema.optional(),
  /** Animated x/y of the mask move only the mask; the children keep their place on the canvas. */
  fixedContent: z.boolean().optional(),
  get children() {
    return z.array(ElementSchema).default([]);
  },
}), 'ClipMaskElement');

export const ElementSchema: z.ZodType<Element, unknown> = z.lazy(() =>
  z.discriminatedUnion('type', [
    GroupElementSchema,
    ClipMaskElementSchema,
    TextElementSchema,
    RectElementSchema,
    EllipseElementSchema,
    LineElementSchema,
    ImageElementSchema,
    VideoElementSchema,
  ]),
) as unknown as z.ZodType<Element, unknown>;

// ---------------------------------------------------------------------------------------------------------------
// Fields

/** Element properties a field can drive. */
export const BindingPropertySchema = z.enum(['text', 'color', 'strokeColor', 'visible', 'image', 'opacity']);
export const BindingSchema = named(z.strictObject({ element: IdSchema, property: BindingPropertySchema }), 'Binding');

const fieldBase = {
  id: FieldIdSchema,
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  /** Leave the value out of labels in timeline and playout UIs (OGraf `hidden`). */
  hidden: z.boolean().default(false),
  /** UI order, lower first. */
  order: z.number().optional(),
  bindings: z.array(BindingSchema).default([]),
};

export const FieldSchema = named(z.discriminatedUnion('type', [
  z.strictObject({
    ...fieldBase,
    type: z.literal('string'),
    default: z.string(),
    maxLength: z.int().positive().optional(),
    multiline: z.boolean().default(false),
    enum: z.array(z.string()).min(1).optional(),
  }),
  z.strictObject({
    ...fieldBase,
    type: z.literal('number'),
    default: z.number(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    integer: z.boolean().default(false),
  }),
  z.strictObject({ ...fieldBase, type: z.literal('boolean'), default: z.boolean() }),
  z.strictObject({ ...fieldBase, type: z.literal('color'), default: ColorSchema }),
  /** A choice among images packaged in the graphic (ADR 0008). Values are asset hashes in the scene. */
  z.strictObject({
    ...fieldBase,
    type: z.literal('image-choice'),
    default: AssetHashSchema,
    choices: z.array(AssetHashSchema).min(1),
  }),
]), 'Field');

// ---------------------------------------------------------------------------------------------------------------
// Timeline

export const EASING_PRESETS = [
  'linear',
  'ease',
  'easeIn',
  'easeOut',
  'easeInOut',
  'easeInQuad',
  'easeOutQuad',
  'easeInOutQuad',
  'easeInCubic',
  'easeOutCubic',
  'easeInOutCubic',
  'easeInQuart',
  'easeOutQuart',
  'easeInOutQuart',
  'easeInExpo',
  'easeOutExpo',
  'easeInOutExpo',
  'easeInBack',
  'easeOutBack',
  'easeInOutBack',
  'hold',
] as const;

export const EasingSchema = named(z.union([
  z.enum(EASING_PRESETS),
  z.strictObject({ cubicBezier: z.tuple([z.number().min(0).max(1), z.number(), z.number().min(0).max(1), z.number()]) }),
]), 'Easing');

export const ANIMATABLE_PROPERTIES = [
  'x',
  'y',
  'scaleX',
  'scaleY',
  'rotation',
  'opacity',
  'width',
  'height',
  'insetTop',
  'insetRight',
  'insetBottom',
  'insetLeft',
  'color',
  'blur',
  'letterSpacing',
] as const;
export const AnimatablePropertySchema = z.enum(ANIMATABLE_PROPERTIES);

/**
 * A keyframe value: an absolute number, a colour (for `color`), or `{ rel }`: the element's designed (base) value
 * plus `rel`, so `{ rel: 0 }` means "where the designer put it" even when data changes that value. On a colour
 * track `{ rel }` means the current designed or field-bound colour (rel is ignored).
 */
export const KeyframeValueSchema = named(z.union([z.number(), ColorSchema, z.strictObject({ rel: z.number() })]), 'KeyframeValue');

/** Properties a staggered track can animate on the parts (characters, words or lines) of a text element. */
export const STAGGER_PROPERTIES = ['x', 'y', 'scaleX', 'scaleY', 'rotation', 'opacity', 'blur', 'color'] as const;

/**
 * Stagger: the track runs once per part of a text element (characters, words or lines), each part starting `each`
 * ms after the previous one. Lines are measured at load/update (never per frame). `within` caps the spread so long
 * text still fits its phase: the last part starts at most `within` ms after the first.
 */
export const StaggerSchema = named(z.strictObject({
  by: z.enum(['char', 'word', 'line']),
  each: nonNegative,
  within: nonNegative.optional(),
  from: z.enum(['start', 'end', 'center']).default('start'),
  /**
   * Character stagger: keep the font's kerning between the characters (each character is its own box, which loses
   * the pair adjustments; they are measured and put back). Absent in scenes made before M7, which keep their look.
   */
  kerning: z.boolean().optional(),
}), 'Stagger');

export const KeyframeSchema = named(z.strictObject({
  /** ms from the start of the phase. */
  time: nonNegative,
  value: KeyframeValueSchema,
  /** Easing of the segment from this keyframe to the next. */
  easing: EasingSchema.default('linear'),
}), 'Keyframe');

export const TrackSchema = named(z.strictObject({
  element: IdSchema,
  property: AnimatablePropertySchema,
  keyframes: z.array(KeyframeSchema).min(1),
  /** Text elements only: run the keyframes per character, word or line, offset in time. */
  stagger: StaggerSchema.optional(),
  /**
   * Colour tracks: the space colours are blended in. sRGB (the default, and what older scenes use) can look muddy
   * between saturated colours; OKLab keeps the perceived lightness and hue even.
   */
  colorSpace: z.enum(['srgb', 'oklab']).optional(),
}), 'Track');

export const PhaseSchema = named(z.strictObject({
  duration: nonNegative,
  tracks: z.array(TrackSchema).default([]),
}), 'Phase');

export const StepSchema = named(z.strictObject({
  id: IdSchema,
  name: z.string().max(200).default(''),
  /** Transition played when entering this step from another step (step 0: only when going back to it). */
  enter: PhaseSchema.optional(),
  /** Hold loop while at this step; it repeats until the next action and never blocks a promise. */
  loop: PhaseSchema.optional(),
}), 'Step');

export const UpdateTransitionSchema = named(z.discriminatedUnion('mode', [
  z.strictObject({ mode: z.literal('cut') }),
  /**
   * Two-layer crossfade over the update duration: the old text or image fades out while the new one fades in;
   * bound colours and opacities blend from the old to the new value.
   */
  z.strictObject({ mode: z.literal('crossfade') }),
  /** Re-run a sub-animation alongside the update. */
  z.strictObject({ mode: z.literal('phase'), phase: PhaseSchema }),
]), 'UpdateTransition');

export const UpdatePhaseSchema = z.strictObject({
  /** Duration of the update animation; auto-size boxes animate old → new measured size over it. */
  duration: nonNegative.default(300),
  easing: EasingSchema.default('easeInOutCubic'),
  tracks: z.array(TrackSchema).default([]),
  /** Per-field transition; fields not listed cut. */
  fields: z.record(FieldIdSchema, UpdateTransitionSchema).default({}),
});

export const CustomActionSchema = named(z.strictObject({
  id: IdSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  /** JSON Schema of the payload (OGraf Action.schema), or null for none. */
  schema: z.record(z.string(), z.unknown()).nullable().default(null),
  phase: PhaseSchema,
}), 'CustomAction');

export const TimelineSchema = z.strictObject({
  in: PhaseSchema,
  /** stepCount = steps.length. Zero steps makes a sting: in, hold for autoOutAfter, then out automatically. */
  steps: z.array(StepSchema).default([{ id: 'step0', name: 'Step 1' }]),
  /** Sting only (no steps): ms between the end of `in` and the start of `out`. */
  autoOutAfter: nonNegative.optional(),
  out: PhaseSchema,
  update: UpdatePhaseSchema.default(() => UpdatePhaseSchema.parse({})),
  customActions: z.array(CustomActionSchema).default([]),
});

// ---------------------------------------------------------------------------------------------------------------
// Assets

export const AssetSchema = named(z.strictObject({
  hash: AssetHashSchema,
  kind: z.enum(['font', 'image', 'video']),
  /** File name inside the package (assets/fonts/ or assets/media/). */
  fileName: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/),
  mime: z.string().min(3),
  /** Fonts: weight range or value as in @font-face (e.g. "100 900", "400"). */
  fontWeight: z.string().optional(),
  fontStyle: z.enum(['normal', 'italic']).optional(),
  /** Licence or source note (e.g. "SIL OFL 1.1"). */
  license: z.string().optional(),
}), 'Asset');

// ---------------------------------------------------------------------------------------------------------------
// Layouts (src/layouts.ts, ADR 0037)

/**
 * What one layout changes on one element. Unlike the element's own building blocks nothing here has a default: an
 * absent property follows the primary layout.
 */
export const OverrideSchema = named(z.strictObject({
  transform: z.strictObject({
    x: z.number().optional(),
    y: z.number().optional(),
    rotation: z.number().optional(),
    scaleX: z.number().optional(),
    scaleY: z.number().optional(),
    originX: z.number().optional(),
    originY: z.number().optional(),
    opacity: z.number().min(0).max(1).optional(),
  }).optional(),
  size: z.strictObject({
    width: Length.optional(),
    height: Length.optional(),
    minWidth: nonNegative.optional(),
    maxWidth: nonNegative.optional(),
    minHeight: nonNegative.optional(),
    maxHeight: nonNegative.optional(),
  }).optional(),
  anchor: AnchorSchema.optional(),
  visible: z.boolean().optional(),
  /** Text elements only. */
  fontSize: z.number().positive().optional(),
  /** Image and video elements only. */
  fit: z.enum(['contain', 'cover', 'fill']).optional(),
}), 'Override');

/**
 * Another layout of the graphic (the canvas is the primary one). Its id is its reduced aspect ratio ("9x16" for
 * 1080×1920). `keyframes` overrides keyframe values by track (`<phasePath>/<element>/<property>`, e.g.
 * "timeline.in/bar/x") and keyframe time in ms ("0", "400"): keyframes not listed follow the primary.
 */
export const LayoutSchema = named(z.strictObject({
  id: z.string().regex(/^[1-9][0-9]{0,4}x[1-9][0-9]{0,4}$/, 'layout ids are the reduced aspect ratio, e.g. 9x16'),
  label: z.string().min(1).max(40).optional(),
  width: z.int().positive().max(7680),
  height: z.int().positive().max(4320),
  overrides: z.record(IdSchema, OverrideSchema).optional(),
  keyframes: z.record(z.string(), z.record(z.string(), KeyframeValueSchema)).optional(),
}), 'Layout');

// ---------------------------------------------------------------------------------------------------------------
// Scene

export const SceneMetaSchema = z.strictObject({
  /** Stable slug; becomes the OGraf manifest id and never changes once saved. No "/". */
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,99}$/),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  author: z.strictObject({ name: z.string().min(1), email: z.string().optional(), url: z.string().optional() }).optional(),
});

const SceneObjectSchema = z.strictObject({
  sceneVersion: z.literal(SCENE_VERSION),
  meta: SceneMetaSchema,
  canvas: CanvasSchema,
  elements: z.array(ElementSchema).default([]),
  fields: z.array(FieldSchema).default([]),
  timeline: TimelineSchema,
  assets: z.array(AssetSchema).default([]),
  /** The graphic's other layouts (multi-layout packages); absent for a single-layout graphic. */
  layouts: z.array(LayoutSchema).max(7).optional(),
});

export type Transform = z.output<typeof TransformSchema>;
export type BlendMode = (typeof BLEND_MODES)[number];
export type CornerRadius = z.output<typeof CornerRadiusSchema>;
export type Size = z.output<typeof SizeSchema>;
export type Anchor = z.output<typeof AnchorSchema>;
export type Fill = z.output<typeof FillSchema>;
export type Stroke = z.output<typeof StrokeSchema>;
export type Shadow = z.output<typeof ShadowSchema>;
export type ShapeStyle = z.output<typeof ShapeStyleSchema>;
export type TextStyle = z.output<typeof TextStyleSchema>;
export type TextOverflow = z.output<typeof TextOverflowSchema>;
export type GroupLayout = z.output<typeof GroupLayoutSchema>;
export type Field = z.output<typeof FieldSchema>;
export type FieldType = Field['type'];
export type Binding = z.output<typeof BindingSchema>;
export type BindingProperty = z.output<typeof BindingPropertySchema>;
export type Easing = z.output<typeof EasingSchema>;
export type EasingPreset = (typeof EASING_PRESETS)[number];
export type AnimatableProperty = (typeof ANIMATABLE_PROPERTIES)[number];
export type KeyframeValue = z.output<typeof KeyframeValueSchema>;
export type Keyframe = z.output<typeof KeyframeSchema>;
export type Track = z.output<typeof TrackSchema>;
export type Stagger = z.output<typeof StaggerSchema>;
export type StaggerProperty = (typeof STAGGER_PROPERTIES)[number];
export type Phase = z.output<typeof PhaseSchema>;
export type Step = z.output<typeof StepSchema>;
export type UpdateTransition = z.output<typeof UpdateTransitionSchema>;
export type UpdatePhase = z.output<typeof UpdatePhaseSchema>;
export type CustomAction = z.output<typeof CustomActionSchema>;
export type Timeline = z.output<typeof TimelineSchema>;
export type Asset = z.output<typeof AssetSchema>;
export type SceneMeta = z.output<typeof SceneMetaSchema>;
export type LayoutOverride = z.output<typeof OverrideSchema>;
export type Layout = z.output<typeof LayoutSchema>;
export type Scene = z.output<typeof SceneObjectSchema>;
/** What a hand-written or editor-produced scene may omit (defaults are filled by parsing). */
export type SceneInput = z.input<typeof SceneObjectSchema>;

// ---------------------------------------------------------------------------------------------------------------
// Cross-reference checks

export function walkElements(elements: readonly Element[], visit: (el: Element, parent: Element | undefined) => void, parent?: Element): void {
  for (const el of elements) {
    visit(el, parent);
    if (el.type === 'group' || el.type === 'clipMask') walkElements(el.children, visit, el);
  }
}

/** The number of OGraf steps the timeline defines. */
export function stepCountOf(scene: Pick<Scene, 'timeline'>): number {
  return scene.timeline.steps.length;
}

const phasesOf = timelinePhases;

const COLOR_PROPS = new Set<AnimatableProperty>(['color']);

export const SceneSchema = SceneObjectSchema.superRefine((scene, ctx) => {
  const issue = (message: string, path: (string | number)[]) => ctx.addIssue({ code: 'custom', message, path });
  const elements = new Map<string, Element>();
  walkElements(scene.elements, (el) => {
    if (elements.has(el.id)) issue(`duplicate element id "${el.id}"`, ['elements']);
    elements.set(el.id, el);
  });
  const assets = new Map(scene.assets.map((a) => [a.hash, a]));
  if (assets.size !== scene.assets.length) issue('duplicate asset hash', ['assets']);
  const fileNames = new Set<string>();
  for (const a of scene.assets) {
    const key = `${a.kind === 'font' ? 'fonts' : 'media'}/${a.fileName}`;
    if (fileNames.has(key)) issue(`duplicate asset file name "${a.fileName}"`, ['assets']);
    fileNames.add(key);
  }
  const needAsset = (hash: string, kind: Asset['kind'], path: (string | number)[]) => {
    const a = assets.get(hash);
    if (!a) issue(`unknown asset ${hash}`, path);
    else if (a.kind !== kind) issue(`asset ${hash} is a ${a.kind}, expected ${kind}`, path);
  };
  walkElements(scene.elements, (el) => {
    if (el.type === 'text') needAsset(el.style.font, 'font', ['elements', el.id, 'style', 'font']);
    if (el.type === 'image') needAsset(el.asset, 'image', ['elements', el.id, 'asset']);
    if (el.type === 'video') needAsset(el.asset, 'video', ['elements', el.id, 'asset']);
    if (el.type === 'text' && el.overflow.mode === 'shrink' && el.overflow.minFontSize > el.style.fontSize)
      issue('minFontSize is larger than fontSize', ['elements', el.id, 'overflow']);
  });

  const fieldIds = new Set<string>();
  scene.fields.forEach((f, i) => {
    if (fieldIds.has(f.id)) issue(`duplicate field id "${f.id}"`, ['fields', i, 'id']);
    fieldIds.add(f.id);
    if (f.type === 'string') {
      if (f.maxLength !== undefined && f.default.length > f.maxLength) issue('default is longer than maxLength', ['fields', i, 'default']);
      if (f.enum && !f.enum.includes(f.default)) issue('default is not one of enum', ['fields', i, 'default']);
    }
    if (f.type === 'number') {
      if (f.minimum !== undefined && f.default < f.minimum) issue('default is below minimum', ['fields', i, 'default']);
      if (f.maximum !== undefined && f.default > f.maximum) issue('default is above maximum', ['fields', i, 'default']);
    }
    if (f.type === 'image-choice') {
      if (!f.choices.includes(f.default)) issue('default is not one of choices', ['fields', i, 'default']);
      f.choices.forEach((c, j) => needAsset(c, 'image', ['fields', i, 'choices', j]));
    }
    f.bindings.forEach((b, j) => {
      const el = elements.get(b.element);
      const path = ['fields', i, 'bindings', j];
      if (!el) return issue(`unknown element "${b.element}"`, path);
      if (b.property === 'text' && el.type !== 'text') issue('text bindings need a text element', path);
      if (b.property === 'image' && (el.type !== 'image' || f.type !== 'image-choice')) issue('image bindings need an image element and an image-choice field', path);
      if ((b.property === 'color' || b.property === 'strokeColor') && f.type !== 'color') issue('colour bindings need a color field', path);
      if (b.property === 'visible' && f.type !== 'boolean') issue('visible bindings need a boolean field', path);
      if (b.property === 'opacity' && f.type !== 'number') issue('opacity bindings need a number field', path);
    });
  });

  const t = scene.timeline;
  if (t.steps.length === 0 && t.autoOutAfter === undefined) issue('a timeline with no steps (a sting) needs autoOutAfter', ['timeline', 'autoOutAfter']);
  if (t.steps.length > 0 && t.autoOutAfter !== undefined) issue('autoOutAfter is only for timelines with no steps', ['timeline', 'autoOutAfter']);
  const stepIds = new Set<string>();
  for (const s of t.steps) {
    if (stepIds.has(s.id)) issue(`duplicate step id "${s.id}"`, ['timeline', 'steps']);
    stepIds.add(s.id);
  }
  const actionIds = new Set<string>();
  for (const a of t.customActions) {
    if (actionIds.has(a.id)) issue(`duplicate custom action id "${a.id}"`, ['timeline', 'customActions']);
    actionIds.add(a.id);
  }
  for (const fid of Object.keys(t.update.fields)) if (!fieldIds.has(fid)) issue(`update transition for unknown field "${fid}"`, ['timeline', 'update', 'fields', fid]);
  for (const [path, phase] of phasesOf(t)) {
    phase.tracks.forEach((tr, i) => {
      if (!elements.has(tr.element)) issue(`track targets unknown element "${tr.element}"`, [path, 'tracks', i]);
      if (tr.stagger) {
        if (elements.get(tr.element)?.type !== 'text') issue('staggered tracks need a text element', [path, 'tracks', i]);
        if (!(STAGGER_PROPERTIES as readonly string[]).includes(tr.property)) issue(`${tr.property} cannot be staggered`, [path, 'tracks', i]);
      }
      if (tr.colorSpace !== undefined && !COLOR_PROPS.has(tr.property)) issue('colorSpace is only for colour tracks', [path, 'tracks', i]);
      let last = -1;
      for (const kf of tr.keyframes) {
        if (kf.time < last) issue('keyframes must be in time order', [path, 'tracks', i]);
        last = kf.time;
        // Colour tracks take colours or { rel } (the current designed or bound colour); others numbers or { rel }.
        const ok = COLOR_PROPS.has(tr.property) ? typeof kf.value !== 'number' : typeof kf.value !== 'string';
        if (!ok) issue(`${tr.property} keyframes need ${COLOR_PROPS.has(tr.property) ? 'colour' : 'numeric'} values`, [path, 'tracks', i]);
        if (kf.time > phase.duration + 1e-9) issue('keyframe after the end of its phase', [path, 'tracks', i]);
      }
    });
  }

  // Layouts: one per aspect (the primary's included), overrides of existing elements and keyframes only.
  const tracks = new Map<string, Track[]>();
  for (const [path, phase] of phasesOf(t)) {
    for (const tr of phase.tracks) {
      const key = trackKey(path, tr);
      tracks.set(key, [...(tracks.get(key) ?? []), tr]);
    }
  }
  const sizes: Array<{ width: number; height: number }> = [scene.canvas];
  (scene.layouts ?? []).forEach((l, i) => {
    const at = ['layouts', i];
    if (l.id !== layoutId(l.width, l.height)) issue(`layout id "${l.id}" is not its aspect ratio ${layoutId(l.width, l.height)}`, [...at, 'id']);
    if (sizes.some((s) => sameAspect(l, s) || sameAspect(s, l))) issue(`layout ${l.id} has the aspect of another layout`, at);
    sizes.push(l);
    for (const [id, o] of Object.entries(l.overrides ?? {})) {
      const el = elements.get(id);
      if (!el) {
        issue(`override of unknown element "${id}"`, [...at, 'overrides', id]);
        continue;
      }
      if (o.fontSize !== undefined && el.type !== 'text') issue('fontSize overrides need a text element', [...at, 'overrides', id]);
      if (o.fit !== undefined && el.type !== 'image' && el.type !== 'video') issue('fit overrides need an image or video element', [...at, 'overrides', id]);
      if (o.fontSize !== undefined && el.type === 'text' && el.overflow.mode === 'shrink' && el.overflow.minFontSize > o.fontSize)
        issue('minFontSize is larger than fontSize', [...at, 'overrides', id]);
    }
    for (const [key, values] of Object.entries(l.keyframes ?? {})) {
      const list = tracks.get(key);
      if (!list) {
        issue(`keyframe override of unknown track "${key}"`, [...at, 'keyframes', key]);
        continue;
      }
      for (const [time, value] of Object.entries(values)) {
        if (!list.some((tr) => tr.keyframes.some((kf) => timeKey(kf.time) === time))) issue(`no keyframe at ${time} ms on "${key}"`, [...at, 'keyframes', key, time]);
        const color = COLOR_PROPS.has(list[0]!.property);
        if (color ? typeof value === 'number' : typeof value === 'string') issue(`${list[0]!.property} keyframes need ${color ? 'colour' : 'numeric'} values`, [...at, 'keyframes', key, time]);
      }
    }
  });
});

/** Parse and validate a scene (defaults filled). Throws a ZodError on invalid input. */
export function parseScene(input: unknown): Scene {
  return SceneSchema.parse(input) as Scene;
}

/** Package path of an asset: assets/fonts/<file> for fonts, assets/media/<file> otherwise. */
export function assetPackagePath(asset: Pick<Asset, 'kind' | 'fileName'>): string {
  return `assets/${asset.kind === 'font' ? 'fonts' : 'media'}/${asset.fileName}`;
}
